import type { WorkspaceStreamTarget } from '@/lib/types/workspace';
import { isLocalTarget } from '@/lib/utils/workspace';

export const MIN_AGENT_WORKSPACE_VERSION = '1.0.6';

// First antioch-sim image that wires the ViewportAnnotator + livestream
// messaging extension. Older remote workspaces have no annotation backend, so
// the Annotate button would open an input-capturing overlay that authors
// nothing — gate it off below this version.
export const MIN_ANNOTATION_WORKSPACE_VERSION = '1.6.11';

interface WorkspaceSupportInput {
  target: WorkspaceStreamTarget | null;
  workspaceVersion: string | null | undefined;
}

/**
 * Return true when the connected workspace can run the agent server.
 *
 * Local targets are always allowed because they point at the developer's
 * checkout. Remote workspaces must report an antioch-sim image version at or
 * above the first version that ships the workspace agent endpoints.
 */
export function isAgentSupportedForWorkspace({ target, workspaceVersion }: WorkspaceSupportInput): boolean {
  if (!target) return false;
  if (isLocalTarget(target)) return true;
  return compareVersion(workspaceVersion, MIN_AGENT_WORKSPACE_VERSION) >= 0;
}

/**
 * Return true when the connected workspace can author viewport annotations.
 *
 * Mirrors :func:`isAgentSupportedForWorkspace`: local checkouts are always
 * allowed; remote workspaces must report an image at or above the first
 * version that ships the annotation backend, otherwise the Annotate overlay
 * would capture input and author nothing.
 */
export function isAnnotationSupportedForWorkspace({ target, workspaceVersion }: WorkspaceSupportInput): boolean {
  if (!target) return false;
  if (isLocalTarget(target)) return true;
  return compareVersion(workspaceVersion, MIN_ANNOTATION_WORKSPACE_VERSION) >= 0;
}

export function compareVersion(left: string | null | undefined, right: string): number {
  const leftParts = parseVersion(left);
  const rightParts = parseVersion(right);
  if (!leftParts || !rightParts) return leftParts ? 1 : rightParts ? -1 : 0;

  for (let index = 0; index < Math.max(leftParts.length, rightParts.length); index++) {
    const leftPart = leftParts[index] ?? 0;
    const rightPart = rightParts[index] ?? 0;
    if (leftPart !== rightPart) return leftPart > rightPart ? 1 : -1;
  }
  return 0;
}

function parseVersion(value: string | null | undefined): number[] | null {
  const raw = value?.trim();
  if (!raw) return null;
  const normalized = raw.replace(/^v/i, '').split(/[+-]/, 1)[0];
  const parts = normalized.split('.');
  if (parts.length === 0) return null;
  const parsed = parts.map((part) => {
    if (!/^\d+$/.test(part)) return null;
    return Number(part);
  });
  return parsed.every((part): part is number => part !== null) ? parsed : null;
}
